import { useEffect, useRef } from "react";

/**
 * Fire a page-view audit whenever the caller-supplied `href` changes.
 *
 * The hook is deliberately router-agnostic — the shared package doesn't
 * declare `react-router-dom` as a dep (it would need to be added to
 * apps/shared/package.json and the lockfile regenerated), so callers own
 * the useLocation() subscription and pass the current href in.
 *
 * The `log` function is expected to be fire-and-forget (see the shell and
 * admin-shell api.ts helpers) — this hook does not await it. Server-side
 * dedupe (see routes/audit-dedupe.ts) protects against React double-renders,
 * so we don't guard the effect against duplicate emissions here — the
 * server is authoritative. We do skip re-emitting for the exact same href
 * within the same mount to keep the request rate low; the server's 30s
 * dedupe is the safety net, not the primary control.
 *
 * @param href      The current page identity — typically `pathname + search`.
 * @param log       Fire-and-forget audit writer.
 * @param enabled   Set false to pause emission (e.g. while org context loads).
 */
export function usePageViewAudit(
  href: string,
  log: (path: string, title?: string) => void,
  enabled = true
): void {
  const lastLoggedHref = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    if (href === lastLoggedHref.current) return;
    lastLoggedHref.current = href;
    // Read the document title lazily — routers often update it after mount,
    // so a microtask defers the read past the current render tick.
    queueMicrotask(() => {
      log(href, typeof document !== "undefined" ? document.title : undefined);
    });
  }, [href, enabled, log]);
}
